import {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {useParams, useHistory, useLocation} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {DatePicker} from "components/DatePicker";
import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {useUserAttendanceSuperAdmin} from "hooks/SuperAdmin/Attendance";
import {getHospitals} from "hooks/SuperAdmin/Hospital";

const formatDurationSeconds = seconds => {
	if (!Number.isFinite(seconds) || seconds <= 0) return "-";

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const hh = String(hours).padStart(2, "0");
	const mm = String(minutes).padStart(2, "0");

	return `${hh} jam ${mm} menit`;
};

export function UserAttendanceDetailPage() {
	const {userId} = useParams();
	const history = useHistory();
	const location = useLocation();
	const [selectedHospital, setSelectedHospital] = useState("");
	const [dateRange, setDateRange] = useState({startDate: undefined, endDate: undefined});
	const [dateDisplay, setDateDisplay] = useState("");
	const [dataLimit, setDataLimit] = useState(10);

	const [defaultHospitalOpts, setDefaultHospitalOpts] = useState([]);

	useEffect(() => {
		const getDefaultHospitalOpts = async () => {
			const response = await getHospitals(null, {page: 1, limit: 10});
			const {rows} = response?.data ?? {};
			const mapped = rows.map(item => ({label: `${item.name}`, value: item.id}));

			setDefaultHospitalOpts([{label: "See All", value: "ALL"}, ...mapped]);
		};

		if (defaultHospitalOpts.length === 0) getDefaultHospitalOpts();
	}, [defaultHospitalOpts.length]);

	const {data, setFilter, isLoading} = useUserAttendanceSuperAdmin(userId);

	// Initialize from list page (date range + search)
	useEffect(() => {
		const initStart = location?.state?.startDate;
		const initEnd = location?.state?.endDate;
		const initSearch = location?.state?.search;

		if (initStart && initEnd) {
			setDateRange({startDate: initStart, endDate: initEnd});
			setDateDisplay(`${dayjs(initStart).format("MM/DD/YYYY")} - ${dayjs(initEnd).format("MM/DD/YYYY")}`);
		}

		if (typeof initSearch === "string") {
			setFilter(prev => ({...prev, search: initSearch}));
		}
	}, [location?.state, setFilter]);

	useEffect(() => {
		setFilter(prev => ({
			...prev,
			hospitalId: selectedHospital === "ALL" ? null : selectedHospital || null,
			date: undefined,
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			page: 1,
		}));
	}, [selectedHospital, dateRange, setFilter]);

	const rows = (data?.data?.rows || []).filter(r => String(r?.userId ?? "") === String(userId ?? ""));

	const columns = useMemo(
		() => [
			{Header: "No", accessor: "idSerial", width: 60, centered: true},
			{Header: "Tanggal", accessor: "date", centered: true},
			{Header: "Jam Masuk", accessor: "checkIn", centered: true},
			{Header: "Jam Pulang", accessor: "checkOut", centered: true},
			{Header: "Durasi Kerja", accessor: "workDuration", centered: true},
			{Header: "Rumah Sakit", accessor: "hospitalName", centered: true},
		],
		[],
	);

	const displayRows = useMemo(() => {
		return rows.map((r, idx) => ({
			...r,
			idSerial: r.idSerial ?? idx + 1,
			date: r.date || (r.checkInDate ? dayjs(r.checkInDate).format("YYYY-MM-DD") : "-"),
			hospitalName: r.hospitalName || (r.hospital ? r.hospital.name : "-"),
			workDuration: (() => {
				const checkInAt = r?.checkInAt;
				const checkOutAt = r?.checkOutAt;

				if (!checkInAt || !checkOutAt) return "-";

				const start = dayjs(checkInAt);
				const end = dayjs(checkOutAt);

				if (!start.isValid() || !end.isValid()) return "-";

				const diffSeconds = end.diff(start, "second");

				return formatDurationSeconds(diffSeconds);
			})(),
		}));
	}, [rows]);

	const userDurationSeconds = data?.data?.summary?.userDurationSeconds ?? 0;

	const totalDurationDisplay = useMemo(() => {
		const hours = Math.floor(userDurationSeconds / 3600);
		const minutes = Math.floor((userDurationSeconds % 3600) / 60);
		const hh = String(hours).padStart(2, "0");
		const mm = String(minutes).padStart(2, "0");

		return `${hh} jam ${mm} menit`;
	}, [userDurationSeconds]);

	const handleBack = () => history.push("/absensi-staff");

	return (
		<DashboardLayout>
			<>
				<div class="text-bold text-dark mb-4">
					<a class="text-dark" style={{cursor: "pointer"}} onClick={handleBack}>
						<span class="icon-ico-back me-2" />
					</a>
					Detail Absensi Staff : {displayRows?.[0]?.staffName || "-"}
				</div>
				<SearchBox
					bottomElement={
						<p class="mb-0 ms-1">
							Total Durasi Absensi: <strong>{totalDurationDisplay}</strong>
						</p>
					}
					rightElement={
						<div className="d-flex align-items-between gap-2">
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Rumah Sakit</label>
								<DropdownAsync
									defaultOptions={defaultHospitalOpts}
									fetchOptions={async (search, callback) => {
										const response = await getHospitals(null, {page: 1, search, limit: 10});
										const {rows} = response?.data ?? {};
										const opts = rows.map(item => ({label: `${item.name}`, value: item.id}));

										callback([{label: "See All", value: "ALL"}, ...opts]);
									}}
									label="Pilih Rumah Sakit"
									onChange={val => setSelectedHospital(val?.value ?? "")}
								/>
							</div>
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Tanggal</label>
								<DatePicker
									initialSettings={{autoUpdateInput: false}}
									singleDatePicker={false}
									onChange={val => {
										const {startDate, endDate} = val || {};

										if (startDate && endDate) {
											setDateRange({startDate, endDate});
											setDateDisplay(
												`${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format(
													"MM/DD/YYYY",
												)}`,
											);
										}
									}}>
									<input
										class="form-control"
										placeholder="Pilih rentang tanggal"
										value={dateDisplay}
									/>
								</DatePicker>
							</div>
						</div>
					}
					onSearch={keyword => {
						setFilter(prev => ({...prev, search: keyword || "", page: 1}));
					}}
				/>

				<BasicTable
					columns={columns}
					currentPage={data?.data?.currentPage || 1}
					data={displayRows}
					hidePagination={true}
					limit={dataLimit}
					loading={isLoading}
					striped={true}
					title="Daftar Absensi"
					totalItems={data?.data?.totalItems ?? rows.length}
					totalPage={1}
					onLimitChange={limit => {
						setDataLimit(limit);
						setFilter(prev => ({...prev, limit}));
					}}
					onPageChange={() => {}}
				/>
			</>
		</DashboardLayout>
	);
}

export default UserAttendanceDetailPage;
